import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { SubmitDealStepProps } from '@/types/submit-deal.types';
import type { RealEstateProperty, PropertyStatus } from '@/types/submit-deal.types';
import { useLanguage } from '@/contexts/LanguageContext';
import { Plus, Trash2, Building2 } from 'lucide-react';

const MAX_PROPERTIES = 10;

function fmt(n: number, currency = 'CAD') {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency, minimumFractionDigits: 0 }).format(n || 0);
}

export const StepRealEstate = ({ formData, updateFormData, errors, clearError, t }: SubmitDealStepProps) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const currency = formData.general?.currency || 'CAD';

  const handleChange = (field: string, value: string | number | boolean) => {
    updateFormData('realEstate', { [field]: value });
    clearError(field);
  };

  const properties: RealEstateProperty[] = formData.realEstate.properties || [];

  // ── Gestion du répéteur de propriétés ────────────────────────────────
  const addProperty = () => {
    if (properties.length >= MAX_PROPERTIES) return;
    const newProperty: RealEstateProperty = {
      id: `property_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      address: '',
      estimatedValue: 0,
      status: 'owned',
      monthlyRent: 0,
      leaseExpirationDate: '',
      remainingLeaseMonths: 0,
      renewalTerms: '',
      assignmentRight: false,
    };
    const updated = [...properties, newProperty];
    updateFormData('realEstate', {
      properties: updated,
      // Synchronise le champ legacy réutilisé ailleurs (ex. CIMPreview
      // highlights page) avec la somme des propriétés possédées.
      realEstateAskPrice: sumOwnedValue(updated),
    });
  };

  const updateProperty = (id: string, field: keyof RealEstateProperty, value: string | number | boolean) => {
    const updated = properties.map(p => (p.id === id ? { ...p, [field]: value } : p));
    updateFormData('realEstate', {
      properties: updated,
      realEstateAskPrice: sumOwnedValue(updated),
    });
  };

  const removeProperty = (id: string) => {
    const updated = properties.filter(p => p.id !== id);
    updateFormData('realEstate', {
      properties: updated,
      realEstateAskPrice: sumOwnedValue(updated),
    });
  };

  const sumOwnedValue = (list: RealEstateProperty[]) =>
    list.filter(p => p.status === 'owned').reduce((sum, p) => sum + (Number(p.estimatedValue) || 0), 0);

  const sumLeasedRent = (list: RealEstateProperty[]) =>
    list.filter(p => p.status === 'leased').reduce((sum, p) => sum + (Number(p.monthlyRent) || 0), 0);

  const totalOwnedValue = sumOwnedValue(properties);
  const totalMonthlyRent = sumLeasedRent(properties);
  const ownedCount = properties.filter(p => p.status === 'owned').length;
  const leasedCount = properties.filter(p => p.status === 'leased').length;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Label>{t.step5.isIncluded} *</Label>
        <RadioGroup
          value={formData.realEstate.isIncluded ? 'yes' : 'no'}
          onValueChange={(value) => handleChange('isIncluded', value === 'yes')}
          className="flex flex-col space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="realEstateYes" />
            <Label htmlFor="realEstateYes" className="font-normal">{t.step5.includedOptions.yes}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="realEstateNo" />
            <Label htmlFor="realEstateNo" className="font-normal">{t.step5.includedOptions.no}</Label>
          </div>
        </RadioGroup>
      </div>

      {formData.realEstate.isIncluded ? (
        <div className="space-y-4">
          {/* AJOUTÉ : Répéteur de propriétés — jusqu'à 10, chacune possédée
              ou louée, avec calcul automatique du total. Auparavant, le
              formulaire ne supportait qu'une seule propriété (un seul
              montant), insuffisant pour des cas comme BâtirPro (3 immeubles). */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {isFr ? 'Propriétés' : 'Properties'} ({properties.length}/{MAX_PROPERTIES})
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isFr
                  ? "Ajoutez chaque immeuble ou terrain séparément — possédé ou loué."
                  : 'Add each building or land parcel separately — owned or leased.'}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addProperty}
              disabled={properties.length >= MAX_PROPERTIES}
            >
              <Plus className="h-4 w-4 mr-1" />
              {isFr ? 'Ajouter une propriété' : 'Add Property'}
            </Button>
          </div>

          {properties.length === 0 ? (
            <p className="text-sm text-muted-foreground italic p-4 border border-dashed rounded-md text-center">
              {isFr ? 'Aucune propriété ajoutée pour le moment.' : 'No properties added yet.'}
            </p>
          ) : (
            <div className="space-y-4">
              {properties.map((property, idx) => (
                <div key={property.id} className="p-4 rounded-md border border-border bg-muted/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {isFr ? 'Propriété' : 'Property'} #{idx + 1}
                    </p>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-destructive h-7 w-7"
                      onClick={() => removeProperty(property.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">{isFr ? 'Adresse' : 'Address'}</Label>
                    <Input
                      value={property.address}
                      onChange={(e) => updateProperty(property.id, 'address', e.target.value)}
                      placeholder={isFr ? '1450, rue Principale, Sainte-Julie, QC' : 'Street address'}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">{isFr ? 'Statut' : 'Status'}</Label>
                    <RadioGroup
                      value={property.status}
                      onValueChange={(v) => updateProperty(property.id, 'status', v as PropertyStatus)}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="owned" id={`${property.id}-owned`} />
                        <Label htmlFor={`${property.id}-owned`} className="font-normal">{isFr ? 'Possédée' : 'Owned'}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="leased" id={`${property.id}-leased`} />
                        <Label htmlFor={`${property.id}-leased`} className="font-normal">{isFr ? 'Louée' : 'Leased'}</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {property.status === 'owned' ? (
                    <div className="space-y-1">
                      <Label className="text-xs">{isFr ? 'Valeur estimée' : 'Estimated Value'}</Label>
                      <CurrencyInput
                        min={0}
                        value={property.estimatedValue || ''}
                        onChange={(v) => updateProperty(property.id, 'estimatedValue', v === '' ? 0 : parseFloat(v))}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{isFr ? 'Loyer mensuel' : 'Monthly Rent'}</Label>
                        <CurrencyInput
                          min={0}
                          value={property.monthlyRent || ''}
                          onChange={(v) => updateProperty(property.id, 'monthlyRent', v === '' ? 0 : parseFloat(v))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{isFr ? "Date d'expiration du bail" : 'Lease Expiration Date'}</Label>
                        <Input
                          type="date"
                          value={property.leaseExpirationDate}
                          onChange={(e) => updateProperty(property.id, 'leaseExpirationDate', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{isFr ? 'Mois restants au bail' : 'Remaining Lease Months'}</Label>
                        <Input
                          type="number"
                          min={0}
                          value={property.remainingLeaseMonths || ''}
                          onChange={(e) => updateProperty(property.id, 'remainingLeaseMonths', parseInt(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{isFr ? 'Conditions de renouvellement' : 'Renewal Terms'}</Label>
                        <Input
                          value={property.renewalTerms}
                          onChange={(e) => updateProperty(property.id, 'renewalTerms', e.target.value)}
                        />
                      </div>
                      <div className="flex items-center space-x-2 md:col-span-2 pt-1">
                        <Checkbox
                          id={`${property.id}-assignment`}
                          checked={property.assignmentRight}
                          onCheckedChange={(v) => updateProperty(property.id, 'assignmentRight', !!v)}
                        />
                        <Label htmlFor={`${property.id}-assignment`} className="font-normal text-sm">
                          {isFr ? 'Droit de cession du bail à un acheteur' : 'Lease assignable to a buyer'}
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* AJOUTÉ : Total calculé automatiquement */}
          {properties.length > 0 && (
            <div className="p-4 rounded-md border-2 border-primary/20 bg-primary/5 space-y-2">
              <p className="text-sm font-semibold">{isFr ? 'Sommaire immobilier' : 'Real Estate Summary'}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between md:block">
                  <span className="text-muted-foreground">
                    {isFr ? `Valeur totale (${ownedCount} possédée${ownedCount > 1 ? 's' : ''})` : `Total value (${ownedCount} owned)`}
                  </span>
                  <span className="font-semibold md:block md:text-lg"> {fmt(totalOwnedValue, currency)}</span>
                </div>
                <div className="flex justify-between md:block">
                  <span className="text-muted-foreground">
                    {isFr ? `Loyer mensuel total (${leasedCount} louée${leasedCount > 1 ? 's' : ''})` : `Total monthly rent (${leasedCount} leased)`}
                  </span>
                  <span className="font-semibold md:block md:text-lg"> {fmt(totalMonthlyRent, currency)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="monthlyRent">{t.step5.monthlyRent}</Label>
            <CurrencyInput
              id="monthlyRent"
              min={0}
              value={formData.realEstate.monthlyRent || ''}
              onChange={(v) => handleChange('monthlyRent', v === '' ? '' : parseInt(v))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="leaseExpirationDate">{t.step5.leaseExpirationDate}</Label>
            <Input
              id="leaseExpirationDate"
              type="date"
              value={formData.realEstate.leaseExpirationDate}
              onChange={(e) => handleChange('leaseExpirationDate', e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};